import { ModalViewModel } from '@/infra/ModalViewModel'
import { action, computed, observable, runInAction } from 'mobx'
import { type Id } from '@/infra/id'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { type Role } from '@/features/iam/roles/Role'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { func, validationContext } from 'validx'
import { task } from 'mobx-task'
import { isNotNil } from '@/utils/object-util'
import { type SearchableUser } from '@/features/iam/users/SearchableUser'
import type {
  AddMemberRoleFn,
  FetchMemberFn,
  ListRolesFn,
} from '@/features/iam'

export class EditUserRoleDialogViewModel {
  readonly modal = new ModalViewModel()

  @observable
  private accessor _selectedUserIds = new Set<Id>()

  @observable
  accessor availableRoles: Role[] = []

  @observable
  accessor selectedRole: Role | undefined = undefined

  @observable
  accessor selectedUser: SearchableUser | undefined = undefined

  @observable
  accessor workspace: Workspace = Workspace.none

  validation = validationContext(this, {
    selectedRole: [
      func((selected) => isNotNil(selected.value) || 'You must select a role.'),
    ],
  })

  constructor(
    private readonly _listRoles: ListRolesFn,
    private readonly _fetchMember: FetchMemberFn,
    private readonly _addMemberRole: AddMemberRoleFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {}

  @computed
  get selectedUserIds() {
    return Array.from(this._selectedUserIds)
  }

  @computed
  get selectedUserName() {
    return this.selectedUser ? this.selectedUser.displayName : null
  }

  @action.bound
  setSelectedRole(role?: Role) {
    this.selectedRole = role
  }

  async showWithUser(
    user: SearchableUser,
    workspace: Workspace,
    signal?: AbortSignal,
  ) {
    runInAction(() => {
      this.selectedUser = user
      this.workspace = workspace
      this._selectedUserIds = new Set([user.publicId])
    })

    const withRetry = this._createAlertRetryWrapper(
      'Loading roles failed',
      signal,
    )
    const loaded = await withRetry(() =>
      Promise.all([
        this._listRoles({ workspaceId: workspace.id }, signal),
        this._fetchMember(
          { workspaceId: workspace.id, userPublicId: user.publicId },
          signal,
        ),
      ]),
    )
      // The retry alert has reported the failure.
      .catch(() => null)

    if (!loaded) {
      return
    }

    const [roles, member] = loaded
    runInAction(() => {
      this.availableRoles = roles
      if (member?.roles.length) {
        this.selectedRole = member.roles[0]
      }
      this.modal.show()
    })
  }

  async open(
    selectedUserIds: Set<Id>,
    workspace: Workspace,
    signal?: AbortSignal,
  ) {
    runInAction(() => {
      this._selectedUserIds = selectedUserIds
      this.workspace = workspace
    })

    const withRetry = this._createAlertRetryWrapper(
      'Loading roles failed',
      signal,
    )
    const roles = await withRetry(() =>
      this._listRoles({ workspaceId: workspace.id }, signal),
    )
      // The retry alert has reported the failure.
      .catch(() => null)
    if (!roles) {
      return
    }

    runInAction(() => {
      this.availableRoles = roles
      this.modal.show()
    })
  }

  @action.bound
  close() {
    this.selectedRole = undefined
    this.selectedUser = undefined
    this.workspace = Workspace.none
    this.validation.reset()
    this.modal.close()
  }

  submit = task.resolved(async () => {
    const role = this.selectedRole
    if (!this.validation.reset().validate().isValid || !role) {
      return
    }

    const alert = this._newAlert()
      .withState('pending')
      .saying('Updating role')
      .autoDismiss()
      .show()

    try {
      await Promise.all(
        this.selectedUserIds.map((userPublicId) =>
          this._addMemberRole(this.workspace.id, userPublicId, role.id),
        ),
      )
    } catch (err) {
      alert.dismiss()
      this._createAlertErrorHandler('Updating the role failed')(err)
    }

    alert
      .withState('success')
      .saying('Success!', 'Role updated successfully!')
      .autoDismiss()
      .show()

    this.close()
  })
}
